/**
 * Walking an equipment hierarchy: flattening it for a table, finding a node,
 * and the path to it. No React here, which is why it is no longer in the
 * component file — a module that exports both loses fast refresh.
 */
import type { EquipmentTreeNode } from '@/api/services/equipment.api';

/** One tree node positioned for rendering as a flat table row. */
export interface FlatRow {
  node: EquipmentTreeNode;
  depth: number;
  hasChildren: boolean;
  /** For each ancestor level, whether that ancestor still has siblings below
   *  it — drives which vertical guide lines continue past this row. */
  guides: boolean[];
}
/**
 * Depth-first walk that emits only the rows currently visible, so collapsed
 * subtrees cost nothing to render. The tree is the source of truth; `expanded`
 * only decides how far down the walk goes.
 */
export function flattenTree(
  nodes: EquipmentTreeNode[],
  expanded: Set<string>,
  depth = 0,
  guides: boolean[] = []
): FlatRow[] {
  const rows: FlatRow[] = [];
  nodes.forEach((node, i) => {
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const isLast = i === nodes.length - 1;
    rows.push({ node, depth, hasChildren, guides });
    if (hasChildren && expanded.has(node.id)) {
      rows.push(...flattenTree(children, expanded, depth + 1, [...guides, !isLast]));
    }
  });
  return rows;
}
/** Every id in the tree that has children — the set "expand all" applies. */
export function collectExpandableIds(nodes: EquipmentTreeNode[]): string[] {
  const ids: string[] = [];
  const walk = (list: EquipmentTreeNode[]) => {
    for (const n of list) {
      const kids = n.children ?? [];
      if (kids.length) {
        ids.push(n.id);
        walk(kids);
      }
    }
  };
  walk(nodes);
  return ids;
}
/** The chain of ancestors leading to `id`, root first, excluding `id`. */
export function pathToNode(
  nodes: EquipmentTreeNode[],
  id: string,
  trail: EquipmentTreeNode[] = []
): EquipmentTreeNode[] | null {
  for (const n of nodes) {
    if (n.id === id) return trail;
    const found = pathToNode(n.children ?? [], id, [...trail, n]);
    if (found) return found;
  }
  return null;
}
/** Locate a subtree by id, so focus mode can re-root the table. */
export function findNode(nodes: EquipmentTreeNode[], id: string): EquipmentTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.children ?? [], id);
    if (found) return found;
  }
  return null;
}
